import * as GeographicLib from 'geographiclib';
import {PolygonPoint} from '../models/polygon-point';
import {PolyUtil} from './poly-util';
import {Logger} from '../../common/utils/logger';
import GeoJSONReader from 'jsts/org/locationtech/jts/io/GeoJSONReader.js'
import UnaryUnionOp from 'jsts/org/locationtech/jts/operation/union/UnaryUnionOp.js'
import Polygonizer from 'jsts/org/locationtech/jts/operation/polygonize/Polygonizer.js'
import InteriorPointArea from 'jsts/org/locationtech/jts/algorithm/InteriorPointArea.js'

/** A point in plane coordinates: degrees of longitude east, latitude north. */
interface PlanePoint {
  x: number;
  y: number;
}

export class SphericalUtil {

  static EARTH_RADIUS_MAJOR = 6371009;

  // static final double EARTH_RADIUS = 6371009;

  static computeTriangleHeight(baseDistance: number, fromAngle: number, toAngle: number, a1: number, a2: number) {
    const factor = (360 - fromAngle) / (360 - toAngle);
    const remain = 360 - toAngle;

    a1 = factor * a1;
    a2 = factor * a2;

    const height = baseDistance * ((Math.tan(a1) * Math.tan(a2)) / (Math.tan(a1) + Math.tan(a2)));
    return height;
  }

  static computeAngle(p1: google.maps.LatLngLiteral, p2: google.maps.LatLngLiteral, p3: google.maps.LatLngLiteral) {
    const h1 = this.computeHeading360(p2, p1);
    const h2 = this.computeHeading360(p2, p3);
    const result = h2 - h1;
    if (h1 < h2) {
      return 360 - Math.abs(result);
    }
    return Math.abs(result);
  }

  static pointInPolygon(targetPoint: google.maps.LatLngLiteral, points: Array<PolygonPoint>) {
    let oddNodes = false;
    let j = points.length - 1;

    for (let i = 0; i < points.length; i++) {
      const ip = points[i];
      const jp = points[j];
      if ((ip.latLng.lng < targetPoint.lng && jp.latLng.lng >= targetPoint.lng
          || jp.latLng.lng < targetPoint.lng && ip.latLng.lng >= targetPoint.lng)
        && (ip.latLng.lat <= targetPoint.lat || jp.latLng.lng <= targetPoint.lat)) {
        const temp = (ip.latLng.lat + (targetPoint.lng - ip.latLng.lng) / (jp.latLng.lng - ip.latLng.lng) * (jp.latLng.lat - ip.latLng.lat) < targetPoint.lat);
        oddNodes = (temp ? !oddNodes : oddNodes); // http://www.howtocreate.co.uk/xor.html
      }
      j = i;
    }
    return oddNodes;
  }

  static fromLatLngToPoint(latitude: number, longitude: number, map: google.maps.Map) {
    const topRight = map.getProjection().fromLatLngToPoint(map.getBounds().getNorthEast());
    const bottomLeft = map.getProjection().fromLatLngToPoint(map.getBounds().getSouthWest());
    const scale = Math.pow(2, map.getZoom());
    const worldPoint = map.getProjection().fromLatLngToPoint(new google.maps.LatLng(latitude, longitude));
    return new google.maps.Point((worldPoint.x - bottomLeft.x) * scale, (worldPoint.y - topRight.y) * scale);
  }

  static fromLatLngToPoint2(latitude: number, longitude: number, map: google.maps.Map, bounds: google.maps.LatLngBounds) {
    const proj = map.getProjection();
    const topRight = proj.fromLatLngToPoint(bounds.getNorthEast());
    const bottomLeft = proj.fromLatLngToPoint(bounds.getSouthWest());
    const scale = Math.pow(2, map.getZoom());
    const worldPoint = proj.fromLatLngToPoint(new google.maps.LatLng(latitude, longitude));
    return new google.maps.Point((worldPoint.x - bottomLeft.x) * scale, (worldPoint.y - topRight.y) * scale);
  }

  static fromPointToLatLng(map: google.maps.Map, x: number, y: number) {

    const bounds = map.getBounds();
    let neBound = bounds.getNorthEast();
    let swBound = bounds.getSouthWest();

    const neBoundInPx = map.getProjection().fromLatLngToPoint(neBound);
    const swBoundInPx = map.getProjection().fromLatLngToPoint(swBound);

    const mapEle = document.getElementById('agmMap');
    const mapWidth = mapEle.offsetWidth;
    const mapHeight = mapEle.offsetHeight;
    // compute the percent of x and y coordinates related to the div containing the map; in my case the screen
    const procX = x/mapWidth;
    const procY = y/mapHeight;

    // compute new coordinates in pixels for lat and lng;
    // for lng : subtract from the right edge of the container the left edge,
    // multiply it by the percentage where the x coordinate was on the screen
    // related to the container in which the map is placed and add back the left boundary
    // you should now have the Lng coordinate in pixels
    // do the same for lat
    const newLngInPx = (neBoundInPx.x - swBoundInPx.x) * procX + swBoundInPx.x;
    const newLatInPx = (swBoundInPx.y - neBoundInPx.y) * procY + neBoundInPx.y;

    // convert from google point in lat lng and have fun :)
    const result = map.getProjection().fromPointToLatLng(new google.maps.Point(newLngInPx, newLatInPx));
    return result;
 }

  private static rotatePolygon(map: google.maps.Map, origin: google.maps.LatLng, path: google.maps.LatLng[], heading: number) {
    const prj = map.getProjection();
    const originPx = prj.fromLatLngToPoint(origin);

    const result = path.map(latLng => {
      const point = prj.fromLatLngToPoint(latLng);
      const rotatedLatLng =  prj.fromPointToLatLng(this.rotatePoint(point,originPx,heading));
      return new google.maps.LatLng(rotatedLatLng.lat(), rotatedLatLng.lng());
    })
    return result;
  }

  private static rotatePoint(point, origin, angle) {
    // const angleRad = angle * Math.PI / 180.0;
    // return new google.maps.Point(
    //   Math.cos(angleRad) * (point.x - origin.x) - Math.sin(angleRad) * (point.y - origin.y) + origin.x,
    //   Math.sin(angleRad) * (point.x - origin.x) + Math.cos(angleRad) * (point.y - origin.y) + origin.y);


    const rad = angle * (Math.PI / 180);

    const x = point.x - origin.x;
    const y = point.y - origin.y;

    return new google.maps.Point(
      x * Math.cos(rad) - y * Math.sin(rad) + origin.x,
      x * Math.sin(rad) + y * Math.cos(rad) + origin.y
    );
  }

  static toRadians(angle) {
    return (Math.PI / 180) * angle;
  }

  static toDegrees(angle) {
    return angle * (180.0 / Math.PI);
  }

  static intersects(lat1, lat2, lng2, lat3, lng3, geodesic: boolean) {
    // Both ends on the same side of lng3.
    if ((lng3 >= 0 && lng3 >= lng2) || (lng3 < 0 && lng3 < lng2)) {
      return false;
    }
    // Point is South Pole.
    if (lat3 <= -Math.PI / 2) {
      return false;
    }
    // Any segment end is a pole.
    if (lat1 <= -Math.PI / 2 || lat2 <= -Math.PI / 2 || lat1 >= Math.PI / 2 || lat2 >= Math.PI / 2) {
      return false;
    }
    if (lng2 <= -Math.PI) {
      return false;
    }
    const linearLat = (lat1 * (lng2 - lng3) + lat2 * lng3) / lng2;
    // Northern hemisphere and point under lat-lng line.
    if (lat1 >= 0 && lat2 >= 0 && lat3 < linearLat) {
      return false;
    }
    // Southern hemisphere and point above lat-lng line.
    if (lat1 <= 0 && lat2 <= 0 && lat3 >= linearLat) {
      return true;
    }
    // North Pole.
    if (lat3 >= Math.PI / 2) {
      return true;
    }

    // Compare lat3 with latitude on the GC/Rhumb segment corresponding to lng3.
    // Compare through a strictly-increasing function (tan() or mercator()) as convenient.
    return geodesic ?
      Math.tan(lat3) >= this.tanLatGC(lat1, lat2, lng2, lng3) :
      this.mercator(lat3) >= this.mercatorLatRhumb(lat1, lat2, lng2, lng3);
  }

  static tanLatGC(lat1, lat2, lng2, lng3) {
    return (Math.tan(lat1) * Math.sin(lng2 - lng3) + Math.tan(lat2) * Math.sin(lng3)) / Math.sin(lng2);
  }

  static mercatorLatRhumb(lat1, lat2, lng2, lng3) {
    return (this.mercator(lat1) * (lng2 - lng3) + this.mercator(lat2) * lng3) / lng2;
  }

  static mercator(lat) {
    return Math.log(Math.tan(lat * 0.5 + Math.PI / 4));
  }

  static clamp(x, low, high) {
    return x < low ? low : (x > high ? high : x);
  }

  static wrap(n, min, max) {
    return (n >= min && n < max) ? n : (SphericalUtil.mod(n - min, max - min) + min);
  }

  static mod(x, m) {
    return ((x % m) + m) % m;
  }

  static computeHeading(from: google.maps.LatLngLiteral, to: google.maps.LatLngLiteral, min: number, max: number) {
    const geod = GeographicLib.Geodesic.WGS84;
    const r = geod.Inverse(from.lat, from.lng, to.lat, to.lng);
    return this.wrap(r.azi1, min, max);
  }

  static computeHeading360(from: google.maps.LatLngLiteral, to: google.maps.LatLngLiteral) {
    const geod = GeographicLib.Geodesic.WGS84;
    const r = geod.Inverse(from.lat, from.lng, to.lat, to.lng);
    return this.wrap(r.azi1, 0, 360);
  }

  static computeFixedHeading(heading) {
    // The nearest multiple of 45 degrees, wrapped: north is both 0 and 360, and
    // measuring only against 0 sent everything from 337.5 up to north-west.
    return SphericalUtil.mod(Math.round(heading / 45) * 45, 360);
  }

  static computeDistanceBetween(from: google.maps.LatLngLiteral, to: google.maps.LatLngLiteral) {
    const geod = GeographicLib.Geodesic.WGS84;
    const r = geod.Inverse(from.lat, from.lng, to.lat, to.lng);

    return Number(r.s12.toFixed(3));
  }

  static computeMidPoint(p1: google.maps.LatLngLiteral, p2: google.maps.LatLngLiteral): google.maps.LatLngLiteral {
    // Halfway the short way round: across the 180th meridian the plain mean
    // of 179.99 and -179.99 put a side's label at longitude 0.
    const dLng = SphericalUtil.wrap(p2.lng - p1.lng, -180, 180);
    return {
      lat: (p1.lat + p2.lat) / 2,
      lng: SphericalUtil.wrap(p1.lng + dLng / 2, -180, 180)
    };
  }

  static calculateNewPointAfterMove(from: google.maps.LatLngLiteral, to: google.maps.LatLngLiteral, distance: number) {
    const heading = this.computeHeading360(from, to);
    return this.computeOffset(to, distance, heading);
  }

  static computeOffset(from: google.maps.LatLngLiteral, distance: number, heading): google.maps.LatLngLiteral {
    const geod = GeographicLib.Geodesic.WGS84;
    const r = geod.Direct(from.lat, from.lng, heading, distance);

    return {
      lat: r.lat2,
      lng: r.lon2
    };
  }

  /**
   * Returns the location of origin when provided with a LatLng destination,
   * meters travelled and original heading. Headings are expressed in degrees
   * clockwise from North. This function returns null when no solution is
   * available.
   * @param to       The destination LatLng.
   * @param distance The distance travelled, in meters.
   * @param heading  The heading in degrees clockwise from north.
   */
  static computeOffsetOrigin(to: google.maps.LatLngLiteral, distance: number, heading: number): google.maps.LatLngLiteral {
    Logger.log('RADs', to, distance, heading);
    heading = SphericalUtil.toRadians(heading);
    distance /= SphericalUtil.EARTH_RADIUS_MAJOR;
    // http://lists.maptools.org/pipermail/proj/2008-October/003939.html
    const n1 = Math.cos(distance);
    const n2 = Math.sin(distance) * Math.cos(heading);
    const n3 = Math.sin(distance) * Math.sin(heading);
    const n4 = Math.sin(SphericalUtil.toRadians(to.lat));
    // There are two solutions for b. b = n2 * n4 +/- sqrt(), one solution results
    // in the latitude outside the [-90, 90] range. We first try one solution and
    // back off to the other if we are outside that range.
    const n12 = n1 * n1;
    const discriminant = n2 * n2 * n12 + n12 * n12 - n12 * n4 * n4;
    if (discriminant < 0) {
      // No real solution which would make sense in LatLng-space.
      return null;
    }
    let b = n2 * n4 + Math.sqrt(discriminant);
    b /= n1 * n1 + n2 * n2;
    const a = (n4 - n2 * b) / n1;
    let fromLatRadians = Math.atan2(a, b);
    if (fromLatRadians < -Math.PI / 2 || fromLatRadians > Math.PI / 2) {
      b = n2 * n4 - Math.sqrt(discriminant);
      b /= n1 * n1 + n2 * n2;
      fromLatRadians = Math.atan2(a, b);
    }
    if (fromLatRadians < -Math.PI / 2 || fromLatRadians > Math.PI / 2) {
      // No solution which would make sense in LatLng-space.
      return null;
    }
    const fromLngRadians = SphericalUtil.toRadians(to.lng) -
      Math.atan2(n3, n1 * Math.cos(fromLatRadians) - n2 * Math.sin(fromLatRadians));
    return {lat: SphericalUtil.toDegrees(fromLatRadians), lng: SphericalUtil.toDegrees(fromLngRadians)};
  }

  static computeLength(path: Array<google.maps.LatLngLiteral>): number {
    if (path.length < 2) {
      return 0;
    }

    let length = 0;
    const prev = path[0];
    let prevLat = prev.lat;
    let prevLng = prev.lng;

    const geod = GeographicLib.Geodesic.WGS84;
    path.forEach((point) => {
      length += geod.Inverse(prevLat, prevLng, point.lat, point.lng).s12;
      prevLat = point.lat;
      prevLng = point.lng;
    });
    return length;
  }

  static computeArea(path: Array<google.maps.LatLngLiteral>): number {
    const geod = GeographicLib.Geodesic.WGS84;
    const calc = geod.Polygon();
    path.forEach((point) => {
      calc.AddPoint(point.lat, point.lng);
    });
    const result = calc.Compute(PolyUtil.isClockwise(path), true);

    // An outline that crosses itself has lobes wound opposite ways, and the
    // signed sum above takes one from the other while the map fills them all:
    // a bow-tie came out near zero, and a corner dragged across the opposite
    // side subtracted its lobe instead of adding it -- into the price too.
    const covered = SphericalUtil.selfCrossingArea(path);
    return covered === null ? Math.abs(result.area) : covered;
  }

  /**
   * The ground a self-crossing outline encloses, each part counted once.
   *
   * The outline is cut where it crosses itself and the faces between the
   * pieces are measured one by one; a face the outline winds around is part of
   * the shape. Null when the outline does not cross or touch itself, which is
   * nearly always, and when it cannot be taken apart -- the signed area is then
   * the best there is.
   */
  private static selfCrossingArea(path: Array<google.maps.LatLngLiteral>): number | null {
    const ring = SphericalUtil.planeRing(path);
    if (ring.length < 4 || !SphericalUtil.ringMeetsItself(ring)) {
      return null;
    }
    try {
      const reader = new GeoJSONReader();
      const outline = reader.read({
        type: 'LineString',
        coordinates: [...ring, ring[0]].map(p => [p.x, p.y]),
      });
      // A union of the outline with itself nodes it where it crosses.
      const polygonizer = new Polygonizer();
      polygonizer.add(UnaryUnionOp.union(outline));
      const faces = polygonizer.getPolygons();
      const count = faces && faces.size ? faces.size() : 0;
      if (!count) {
        return null;
      }
      let total = 0;
      for (let i = 0; i < count; i++) {
        const face = faces.get(i);
        const inside = InteriorPointArea.getInteriorPoint(face);
        if (!inside || SphericalUtil.windingNumber(ring, inside) === 0) {
          continue;
        }
        total += SphericalUtil.faceArea(face.getExteriorRing().getCoordinates());
        for (let h = 0; h < face.getNumInteriorRing(); h++) {
          total -= SphericalUtil.faceArea(face.getInteriorRingN(h).getCoordinates());
        }
      }
      return total;
    } catch (e) {
      return null;
    }
  }

  /** Geodesic area of one face, whose coordinates are plane points. */
  private static faceArea(coordinates: PlanePoint[]): number {
    const calc = GeographicLib.Geodesic.WGS84.Polygon();
    coordinates.forEach(c => calc.AddPoint(c.y, c.x));
    return Math.abs(calc.Compute(false, true).area);
  }

  /**
   * A path as plane points, longitudes unwrapped about the first so a shape
   * across the antimeridian stays in one piece, and repeated points dropped --
   * the copy of the first point a closed shape ends with among them.
   */
  private static planeRing(path: Array<google.maps.LatLngLiteral>): PlanePoint[] {
    const ring: PlanePoint[] = [];
    if (!path || !path.length) {
      return ring;
    }
    const lng0 = path[0].lng;
    path.forEach(point => {
      const x = lng0 + SphericalUtil.wrap(point.lng - lng0, -180, 180);
      const last = ring[ring.length - 1];
      if (!last || last.x !== x || last.y !== point.lat) {
        ring.push({x, y: point.lat});
      }
    });
    while (ring.length > 1
      && ring[0].x === ring[ring.length - 1].x && ring[0].y === ring[ring.length - 1].y) {
      ring.pop();
    }
    return ring;
  }

  /**
   * Whether two sides of a ring that are not neighbours meet. Sides are swept
   * in order of their west end and compared only while they overlap east-west,
   * because this runs on every redraw and a parcel can have thousands of sides.
   */
  private static ringMeetsItself(ring: PlanePoint[]): boolean {
    const n = ring.length;
    const sides = ring.map((a, i) => {
      const b = ring[(i + 1) % n];
      return {
        i, a, b,
        minX: Math.min(a.x, b.x), maxX: Math.max(a.x, b.x),
        minY: Math.min(a.y, b.y), maxY: Math.max(a.y, b.y),
      };
    }).sort((s, t) => s.minX - t.minX);
    for (let k = 0; k < n; k++) {
      const s = sides[k];
      for (let m = k + 1; m < n && sides[m].minX <= s.maxX; m++) {
        const t = sides[m];
        const apart = Math.abs(s.i - t.i);
        if (apart === 1 || apart === n - 1 || t.minY > s.maxY || t.maxY < s.minY) {
          continue;
        }
        if (SphericalUtil.segmentsMeet(s.a, s.b, t.a, t.b)) {
          return true;
        }
      }
    }
    return false;
  }

  /** Whether two segments cross or touch. */
  private static segmentsMeet(p1: PlanePoint, p2: PlanePoint, q1: PlanePoint, q2: PlanePoint): boolean {
    const d1 = SphericalUtil.orient(q1, q2, p1);
    const d2 = SphericalUtil.orient(q1, q2, p2);
    const d3 = SphericalUtil.orient(p1, p2, q1);
    const d4 = SphericalUtil.orient(p1, p2, q2);
    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
      return true;
    }
    const within = (a: PlanePoint, b: PlanePoint, p: PlanePoint) =>
      p.x >= Math.min(a.x, b.x) && p.x <= Math.max(a.x, b.x)
      && p.y >= Math.min(a.y, b.y) && p.y <= Math.max(a.y, b.y);
    return (d1 === 0 && within(q1, q2, p1)) || (d2 === 0 && within(q1, q2, p2))
      || (d3 === 0 && within(p1, p2, q1)) || (d4 === 0 && within(p1, p2, q2));
  }

  /** Twice the signed area of the triangle a-b-c: positive when c is left of a to b. */
  private static orient(a: PlanePoint, b: PlanePoint, c: PlanePoint): number {
    return (b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y);
  }

  /** How many times a ring winds around a point, counter-clockwise positive. */
  private static windingNumber(ring: PlanePoint[], p: PlanePoint): number {
    let winding = 0;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      if (a.y <= p.y) {
        if (b.y > p.y && SphericalUtil.orient(a, b, p) > 0) {
          winding++;
        }
      } else if (b.y <= p.y && SphericalUtil.orient(a, b, p) < 0) {
        winding--;
      }
    }
    return winding;
  }

  static closestPointOnLine(point, lineStart, lineEnd) {
    const projection = google.maps.geometry.spherical.computeOffset(lineStart, google.maps.geometry.spherical.computeDistanceBetween(lineStart, point), google.maps.geometry.spherical.computeHeading(lineStart, lineEnd));
    if (google.maps.geometry.spherical.computeDistanceBetween(projection, lineEnd) < google.maps.geometry.spherical.computeDistanceBetween(lineStart, point)) {
      return projection.toJSON();
    } else {
      return lineStart;
    }
  }

  /**
   * The foot of the perpendicular from `point` to the side from `lineStart`
   * to `lineEnd` -- the point of the side nearest to it -- on the WGS84
   * ellipsoid the app measures on. Null when the foot falls beyond either end
   * (the corner there is nearer) or the side has no length.
   *
   * It used to be the point of the side as far from its start as `point` is,
   * which is not the foot: a vertex snapped to a side landed further along it
   * than the user aimed, the more so the further off the side and the nearer
   * its start the cursor was, and the distance to the side came out too long.
   */
  static computePerpendicularBasePoint(point: google.maps.LatLngLiteral, lineStart: google.maps.LatLngLiteral,
                                       lineEnd: google.maps.LatLngLiteral): google.maps.LatLngLiteral | null {
    const geod = GeographicLib.Geodesic.WGS84;
    const side = geod.Inverse(lineStart.lat, lineStart.lng, lineEnd.lat, lineEnd.lng);
    if (!(side.s12 > 0)) {
      return null;
    }
    // How far along the side the foot is. Distances and directions from where
    // the side is looked at from are exact, so the first guess, from the
    // start, is already right to far under a millimetre across a site; a
    // second look from that guess makes it so on a side kilometres long too.
    const toPoint = geod.Inverse(lineStart.lat, lineStart.lng, point.lat, point.lng);
    let along = toPoint.s12 * Math.cos(SphericalUtil.toRadians(toPoint.azi1 - side.azi1));
    const guess = geod.Direct(lineStart.lat, lineStart.lng, side.azi1, along);
    const fromGuess = geod.Inverse(guess.lat2, guess.lon2, point.lat, point.lng);
    along += fromGuess.s12 * Math.cos(SphericalUtil.toRadians(fromGuess.azi1 - guess.azi2));
    // A hair either side of an end is rounding, not beyond it.
    const slack = 1e-6;
    if (!(along >= -slack && along <= side.s12 + slack)) {
      return null;
    }
    if (along <= 0) {
      return {lat: lineStart.lat, lng: lineStart.lng};
    }
    if (along >= side.s12) {
      return {lat: lineEnd.lat, lng: lineEnd.lng};
    }
    const foot = geod.Direct(lineStart.lat, lineStart.lng, side.azi1, along);
    return {lat: foot.lat2, lng: foot.lon2};
  }

  static computeDistanceBetween2(p1: google.maps.LatLngLiteral, p2: google.maps.LatLngLiteral) {
    return google.maps.geometry.spherical.computeDistanceBetween(p1, p2);
  }

  static computePerpendicularBasePointOnPath(point: google.maps.LatLngLiteral, lineStart: google.maps.LatLngLiteral, lineEnd: google.maps.LatLngLiteral) {
    const b1 = this.computePerpendicularBasePoint(point, lineStart, lineEnd);

    let intersection;
    if(b1) {
      const d = google.maps.geometry.spherical.computeDistanceBetween(point, b1);
      if(d < 0.0001) {
        intersection = b1;
      }
    }
    return intersection;
  }

  static computePerpendicularBasePoint2(point: google.maps.LatLngLiteral, lineStart: google.maps.LatLngLiteral, lineEnd: google.maps.LatLngLiteral){
    const cp = this.closestPointBetween2D([point.lng, point.lat], [lineStart.lng, lineStart.lat], [lineEnd.lng, lineEnd.lat]);
    return {
      lng: cp[0],
      lat: cp[1]
    }
  }


  private static closestPointBetween2D(P, A, B) {
    const _zero2D = [0, 0]
    const v = [B[0] - A[0], B[1] - A[1]]
    const u = [A[0] - P[0], A[1] - P[1]]
    const vu = v[0] * u[0] + v[1] * u[1]
    const vv = v[0] ** 2 + v[1] ** 2
    const t = -vu / vv
    if (t >= 0 && t <= 1) return this._vectorToSegment2D(t, _zero2D, A, B)
    const g0 = this._sqDiag2D(this._vectorToSegment2D(0, P, A, B))
    const g1 = this._sqDiag2D(this._vectorToSegment2D(1, P, A, B))
    return g0 <= g1 ? A : B
  }

  static _vectorToSegment2D(t, P, A, B) {
    return [
      (1 - t) * A[0] + t * B[0] - P[0],
      (1 - t) * A[1] + t * B[1] - P[1],
    ]
  }

  static _sqDiag2D(P) { return P[0] ** 2 + P[1] ** 2 }

  static getCirclePoints(center: google.maps.LatLngLiteral, radius: number) {
    const points = [];
    for (let i = 0; i <= 360; i++) {
      const p = SphericalUtil.computeOffset(center, radius, i);
      points.push(p);
    }
    return points;
  }

  static compute3rdPointInRectangle(p1: google.maps.LatLngLiteral, p2: google.maps.LatLngLiteral, cp: google.maps.LatLngLiteral): google.maps.LatLngLiteral {
    const mid = {lat: (p1.lat + p2.lat) / 2, lng: (p1.lng + p2.lng) / 2};
    const h1 = SphericalUtil.computeHeading360(p1, p2);
    // The depth is how far the cursor stands off the base line, not how far it
    // is from the corner: with the cursor 2 m along and 5 m out, the straight
    // distance made the rectangle 5.39 m deep and its area 7.7% too large, and a
    // corner snapped to an existing point pushed the far side past it.
    const offBase = SphericalUtil.toRadians(SphericalUtil.computeHeading360(p2, cp) - h1);
    const distance = SphericalUtil.computeDistanceBetween(p2, cp) * Math.abs(Math.sin(offBase));
    const h2 = SphericalUtil.mod(h1 + 180, 360);
    const newMin = Math.min(h1, h2);
    const newMax = Math.max(h1, h2);
    const heading = SphericalUtil.computeHeading360(mid, cp);
    const angle = (heading > newMax || heading < newMin) ? -90 : 90;
    const newHeading = SphericalUtil.mod(newMin + angle, 360);
    return SphericalUtil.computeOffset(p2, distance, newHeading);
  }

  static computeFourthRectanglePoint(a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral, c: google.maps.LatLngLiteral): google.maps.LatLngLiteral {
    const cp = {lat: (a.lat + c.lat) / 2, lng: (a.lng + c.lng) / 2};
    const x4 = (2 * cp.lat) - b.lat;
    const y4 = (2 * cp.lng) - b.lng;
    return {lat: x4, lng: y4};
  }

  static computePolygonWidth(path: google.maps.LatLngLiteral[]): number {
    let max = -180;
    let min = 180;
    let lat = 0;
    path.forEach(point => {
      if (point.lng > max) {
        max = point.lng;
        lat = point.lat;
      }
    });

    path.forEach(point => {
      if (point.lng < min) {
        min = point.lng;
      }
    });

    return SphericalUtil.computeDistanceBetween({lat, lng: min}, {lat, lng: max});
  }

  static computePolygonDiagonal(path: google.maps.LatLngLiteral[]): number {
    let maxLat = -180;
    let maxLng = -180;
    let minLat = 180;
    let minLng = 180;
    let lat = 0;
    let lng = 0;
    path.forEach(point => {
      if (point.lat > maxLat) {
        maxLat = point.lat;
        lng = point.lng;
      }
      if (point.lng > maxLng) {
        maxLng = point.lng;
        lat = point.lat;
      }
    });

    path.forEach(point => {
      if (point.lng < minLng) {
        minLng = point.lng;
      }
      if (point.lat < minLat) {
        minLat = point.lat;
      }
    });

    const d1 = SphericalUtil.computeDistanceBetween({lat, lng: minLng}, {lat, lng: maxLng});
    const d2 = SphericalUtil.computeDistanceBetween({lat: minLat, lng}, {lat: maxLat, lng});
    return Math.sqrt(Math.pow(d1, 2) + Math.pow(d2, 2));
  }

  static boundsOfTile(x: number, y: number, zoom: number): google.maps.LatLngBounds {
    const noTiles = 1 << zoom
    const longitudeSpan = 360.0 / noTiles
    const longitudeMin = -180.0 + x * longitudeSpan

    const mercatorMax = 180 - y / noTiles * 360.0
    const mercatorMin = 180 - (y + 1) / noTiles * 360.0
    const latitudeMax = this.toLatitude(mercatorMax)
    const latitudeMin = this.toLatitude(mercatorMin)

    return new google.maps.LatLngBounds(new google.maps.LatLng(latitudeMin, longitudeMin), new google.maps.LatLng(latitudeMax, longitudeMin + longitudeSpan))
  }

  static toLatitude(mercator: number): number {
    const radians = Math.atan(Math.exp(this.toRad(mercator)))
    return this.toDeg(2 * radians) - 90
  }

  static toRad(Value) {
    return Value * Math.PI / 180;
  }

  static toDeg(radians) {
    return radians * (180 / Math.PI);
  }

  static getBoundsZoomLevel(bounds: google.maps.LatLngBounds, mapWidth: number, mapHeight: number) {
    const WORLD_DIM = {height: 256, width: 256};
    const ZOOM_MAX = 21;

    function latRad(lat) {
      const sin = Math.sin(lat * Math.PI / 180);
      const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
      return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    }

    function zoom(mapPx, worldPx, fraction) {
      return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

    const latZoom = zoom(mapHeight, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapWidth, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  }

  static tileCoords(lat: number, lng: number, zoom: number) {

    // const scale = 1 << zoom;
    //
    // const worldCoordinate = this.project(lat, lng);

    const n = 2.0 ** zoom
    const x = ((lng + 180.0) / 360.0 * n).toInt();
    const y = ((1.0 - Math.asinh(Math.tan(this.toRadians(lat))) / Math.PI) / 2.0 * n).toInt();

    return {
      x,
      y,
      zoom
    };
  }

  static project(lat: number, lng: number) {
    let siny = Math.sin((lat * Math.PI) / 180);

    // Truncating to 0.9999 effectively limits latitude to 89.189. This is
    // about a third of a tile past the edge of the world tile.
    siny = Math.min(Math.max(siny, -0.9999), 0.9999);

    return new google.maps.Point(
      256 * (0.5 + lng / 360),
      256 * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI))
    );
  }

  static getTilesInBounds(viewPort, zoom) {

    const bounds = viewPort;
    const boundingBoxes = [];
    const boundsNeLatLng = bounds.getNorthEast();
      const boundsSwLatLng = bounds.getSouthWest();
    const boundsNwLatLng = new google.maps.LatLng(boundsNeLatLng.lat(), boundsSwLatLng.lng());
      const boundsSeLatLng = new google.maps.LatLng(boundsSwLatLng.lat(), boundsNeLatLng.lng());
    const tiles = [];
      const tileCoordinateNw = this.tileCoords(boundsNwLatLng.lat(), boundsNwLatLng.lng(), zoom);
    const tileCoordinateSe = this.tileCoords(boundsSeLatLng.lat(), boundsSeLatLng.lng(), zoom);
      let tileColumns = tileCoordinateSe.x - tileCoordinateNw.x + 1;
    let tileRows = tileCoordinateSe.y - tileCoordinateNw.y + 1;
      const zfactor = Math.pow(2, zoom);
    const minX = tileCoordinateNw.x;
      let minY = tileCoordinateNw.y;

    while (tileRows--) {
      while (tileColumns--) {
        tiles.push({
          x: minX + tileColumns,
          y: minY,
          zoom
        });
      }

      minY++;
      tileColumns = tileCoordinateSe.x - tileCoordinateNw.x + 1;
    }

    return tiles;
  }

  static latLng2point(latLng, max) {

    return {
      x: (latLng.lng + 180) * (max / 360),
      y: (max / 2) - (max * Math.log(Math.tan((Math.PI / 4) + ((latLng.lat * Math.PI / 180) / 2))) / (2 * Math.PI))
    };
  }

  // static poly_gm2svg(gmPaths, fx) {
  //
  //   let point,
  //     gmPath,
  //     svgPath,
  //     svgPaths = [],
  //     minX = 256,
  //     minY = 256,
  //     maxX = 0,
  //     maxY = 0;
  //
  //   for (let pp = 0; pp < gmPaths.length; ++pp) {
  //     gmPath = gmPaths[pp], svgPath = [];
  //     for (let p = 0; p < gmPath.length; ++p) {
  //       point = this.latLng2point(fx(gmPath[p]));
  //       minX = Math.min(minX, point.x);
  //       minY = Math.min(minY, point.y);
  //       maxX = Math.max(maxX, point.x);
  //       maxY = Math.max(maxY, point.y);
  //       // svgPath.push([point.x, point.y].join(','));
  //       const command = (p === 0) ? 'M' : 'L';
  //       svgPath.push(`${command}${point.x},${point.y}`);
  //     }
  //
  //     svgPaths.push(svgPath.join(' '))
  //
  //   }
  //   return {
  //     path: 'M' + svgPaths.join('z M') + 'z',
  //     x: minX,
  //     y: minY,
  //     width: maxX - minX,
  //     height: maxY - minY
  //   };
  //
  // }



  static poly_gm2svg(gmPath: google.maps.LatLngLiteral[]) {

    const max = 10000;
    let point,
      minX = max,
      minY = max,
      maxX = 0,
      maxY = 0;

    const svgPathCommands = gmPath.map((p, index) => {
      point = this.latLng2point(p, max);
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);

      const command = (index === 0) ? 'M' : 'L';
      return `${command}${point.x},${point.y}`;
    })

    return {
      path: svgPathCommands.join(' '),
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };

  }

  static polygonToSvg(){

  }


  static equalsLatLng(p1: google.maps.LatLngLiteral, p2: google.maps.LatLngLiteral): boolean {
    return p1.lat === p2.lat && p1.lng === p2.lng;
  }
}
